import React, {createContext, useContext, useState, useCallback, useMemo} from 'react';
import {CHROME_PRESETS} from './chrome';

const STORAGE_KEY = 'nofam.chrome.mode';
const SIDEBAR_STORAGE_KEY = 'nofam.sidebar.collapsed';

const ThemeModeContext = createContext(null);

const readStored = () => {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return v === 'light' ? 'light' : 'dark';
    } catch {
        return 'dark';
    }
};

const readSidebarStored = () => {
    try {
        return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

export const ThemeModeProvider = ({children}) => {
    const [mode, setMode] = useState(readStored);
    const [sidebarCollapsed, setSidebarCollapsedState] = useState(readSidebarStored);

    const setChromeMode = useCallback((next) => {
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch {
            /* storage unavailable */
        }
        setMode(next);
    }, []);

    const toggleChromeMode = useCallback(() => {
        setChromeMode(mode === 'dark' ? 'light' : 'dark');
    }, [mode, setChromeMode]);

    const setSidebarCollapsed = useCallback((next) => {
        try {
            localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
        } catch {
            /* storage unavailable */
        }
        setSidebarCollapsedState(next);
    }, []);

    const toggleSidebar = useCallback(() => {
        setSidebarCollapsed(!sidebarCollapsed);
    }, [sidebarCollapsed, setSidebarCollapsed]);

    const chrome = useMemo(() => CHROME_PRESETS[mode], [mode]);

    return (
        <ThemeModeContext.Provider value={{
            mode, chrome, setChromeMode, toggleChromeMode,
            sidebarCollapsed, setSidebarCollapsed, toggleSidebar,
        }}>
            {children}
        </ThemeModeContext.Provider>
    );
};

export const useChromeMode = () => {
    const ctx = useContext(ThemeModeContext);
    if (!ctx) {
        return {
            mode: 'dark',
            chrome: CHROME_PRESETS.dark,
            setChromeMode: () => {},
            toggleChromeMode: () => {},
            sidebarCollapsed: false,
            setSidebarCollapsed: () => {},
            toggleSidebar: () => {},
        };
    }
    return ctx;
};
